/**
 * H14 – browser storage belongs to one user.
 *
 * Everything WRAP keeps in sessionStorage / localStorage uses the `wrap.` prefix. It is cleared on
 * logout, when the session expires, and whenever a different user signs in on the same tab, so
 * user B never sees anything user A left behind (an in-progress hired equipment entry, etc.).
 */

const PREFIX = 'wrap.';
const OWNER_KEY = 'wrap.owner';

function clearPrefixed(storage: Storage | undefined): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage blocked (private mode, policy) – nothing to clear.
  }
}

function safeStorage(kind: 'sessionStorage' | 'localStorage'): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window[kind] : undefined;
  } catch {
    return undefined;
  }
}

export function clearWrapStorage(): void {
  clearPrefixed(safeStorage('sessionStorage'));
  clearPrefixed(safeStorage('localStorage'));
}

/** Call when a user is known. Clears storage left by a different user, then records this one. */
export function claimWrapStorage(userId: number | string): void {
  const owner = String(userId);
  const session = safeStorage('sessionStorage');
  const local = safeStorage('localStorage');
  try {
    const previous = session?.getItem(OWNER_KEY) ?? local?.getItem(OWNER_KEY) ?? null;
    if (previous !== owner) {
      clearWrapStorage();
    }
    session?.setItem(OWNER_KEY, owner);
    local?.setItem(OWNER_KEY, owner);
  } catch {
    // ignore
  }
}
